/**
 * Mermaidダイアグラム描画コンポーネント
 *
 * Markdownコンテンツ内のMermaid図を描画する
 */

import { useState, useEffect, useRef } from "react";
import { Box, Text } from "@mantine/core";
import mermaid from "mermaid";

/**
 * Mermaid初期化（常にneutralテーマ＋黒テキストで描画）
 */
const initializeMermaid = () => {
  mermaid.initialize({
    startOnLoad: false,
    theme: "neutral",
    securityLevel: "loose",
    themeVariables: {
      // テキストを常に黒系で確実に視認可能にする
      primaryTextColor: "#1a1a1a",
      secondaryTextColor: "#333333",
      tertiaryTextColor: "#333333",
      lineColor: "#555555",
      textColor: "#1a1a1a",
      // シーケンス図用
      actorTextColor: "#1a1a1a",
      signalTextColor: "#1a1a1a",
      labelTextColor: "#1a1a1a",
      loopTextColor: "#1a1a1a",
      noteTextColor: "#1a1a1a",
    },
  });
};

interface MermaidDiagramProps {
  code: string;
}

/**
 * Mermaidダイアグラムコンポーネント
 */
export function MermaidDiagram({ code }: MermaidDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    // カラーモード検出
    const colorScheme = document.documentElement.getAttribute("data-mantine-color-scheme");
    setIsDark(colorScheme === "dark");

    const renderDiagram = async () => {
      if (!containerRef.current) return;
      try {
        initializeMermaid();

        const id = `mermaid-${Date.now()}`;
        const { svg } = await mermaid.render(id, code);
        setSvg(svg);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Mermaid描画エラー");
      }
    };
    renderDiagram();
  }, [code]);

  if (error) {
    return (
      <Box
        style={{
          padding: "1rem",
          backgroundColor: "#fff3cd",
          border: "1px solid #ffc107",
          borderRadius: "4px",
        }}
      >
        <Text size="sm" c="orange.8">
          図の描画に失敗しました: {error}
        </Text>
        <pre style={{ fontSize: "0.8rem", overflow: "auto" }}>{code}</pre>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      dangerouslySetInnerHTML={{ __html: svg }}
      style={{
        display: "flex",
        justifyContent: "center",
        margin: "1rem 0",
        padding: "1rem",
        overflow: "auto",
        // ライト: 薄いグレー、ダーク: 落ち着いたグレー
        backgroundColor: isDark ? "#4b5563" : "#f3f4f6",
        borderRadius: "8px",
      }}
    />
  );
}
